/**
 * Вики JustKiwi — точка входа (собирается в wiki/app.min.js).
 *
 * Порядок запуска: сессия и настройки → каталог тем и статей → каркас страницы →
 * роутер. Если вики закрыта на техработы, посетитель без права редактора видит заглушку.
 * Экраны читателя (главная, статья, раздел для разработчиков) входят в основную сборку;
 * редактор — отдельная часть, её код скачивают только те, кто туда заходит. Управление вики
 * и переводы живут в админ-панели: прежние адреса /wiki/admin и /wiki/translations уводят туда.
 */
import "./icons.js";
import { can, loadCatalog, loadSession, wiki } from "./data.js";
import { startRouter } from "./router.js";
import { markHeaderLinks, mountShell, setMain, showInReader, showScreen } from "./shell.js";
import { renderArticle } from "./article.js";
import { renderHome } from "./home.js";
import {
  devSectionScreen, errorScreen, forbiddenScreen, maintenanceScreen, notFoundScreen, notificationsScreen,
} from "./pages.js";

/** Разделы админ-панели, куда переехали прежние экраны вики «Управление» и «Переводы». */
const ADMIN_PANEL_PATHS = { admin: "/admin/#/wiki", translations: "/admin/#/translations" };

/** Номер последнего перехода: результат устаревшего (медленного) перехода не показывается. */
let renderToken = 0;

/**
 * Собрать экран по маршруту: { node, title, reader?, onShown? }. reader — показать в
 * раскладке чтения с меню статей; onShown — действия после появления на странице.
 */
async function buildScreen(route) {
  switch (route.view) {
    case "home":
      return renderHome();
    case "article":
      return renderArticle(route.slug);
    case "dev":
      return can.dev ? devSectionScreen() : notFoundScreen();
    case "notifications":
      return notificationsScreen();
    case "editor":
      if (!can.edit) return forbiddenScreen();
      return (await import("./editor.js")).renderEditor(route.slug);
    default:
      return notFoundScreen();
  }
}

/** Отрисовать экран маршрута и восстановить прокрутку (или прокрутить к разделу из адреса). */
async function renderRoute(route, { scrollY = 0, hash = "" } = {}) {
  if (route.view === "login") {
    // Прямой адрес /wiki/login (старые ссылки) — вход в кабинете с возвратом на главную вики.
    location.replace("/cabinet/?next=%2Fwiki%2F");
    return;
  }
  if (ADMIN_PANEL_PATHS[route.view]) {
    location.replace(ADMIN_PANEL_PATHS[route.view]);
    return;
  }
  const token = ++renderToken;
  let screen;
  try {
    screen = await buildScreen(route);
  } catch (error) {
    console.error(error);
    screen = errorScreen(null);
  }
  if (token !== renderToken) return;
  await showScreen(() => {
    if (screen.reader) showInReader(screen.node, screen.reader);
    else setMain(screen.node);
  });
  document.title = screen.title ? `${screen.title} — Вики JustKiwi` : "Вики JustKiwi";
  markHeaderLinks();
  restoreScroll(scrollY, hash);
  if (screen.onShown) screen.onShown();
}

/** Прокрутка после смены экрана: к разделу из адреса (#якорь) или на запомненное место. */
function restoreScroll(scrollY, hash) {
  const target = hash && hash.length > 1 ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
  if (target) target.scrollIntoView({ block: "start" });
  else window.scrollTo({ top: scrollY, behavior: "instant" });
}

async function bootstrap() {
  await loadSession();
  const open = await loadCatalog();
  if (!open || (wiki.settings.maintenance && !can.edit)) {
    mountShell({ search: false });
    setMain(maintenanceScreen().node);
    document.title = "Техобслуживание — Вики JustKiwi";
    return;
  }
  mountShell();
  startRouter(renderRoute);
}

bootstrap();
